<template>
  <div
    :style="inputStyle"
    class="input-wrapper"
  >
    <div
      v-if="label"
      class="input-label"
      :disabled="disabled"
    >
      {{ label }}
      <AuTooltip
        v-if="tooltip?.length"
        :description="tooltip"
      />
    </div>

    <Datepicker
      v-if="type === 'Date'"
      :class="{'required-error' : reqErrorDate}"
      :model-value="modelValue"
      locale="ru"
      auto-apply
      class="date-input"
      :min-date="minDate"
      :max-date="maxDate"
      :format="format"
      :enable-time-picker="enableTimePicker"
      :enable-minutes="enableMinutes"
      :placeholder="placeholder"
      :readonly="readonly"
      teleport
      @update:modelValue="value => input(value)"
    >
      <template #clear-icon="{ clear }">
        <div @click="clear">
          ×
        </div>
      </template>
    </Datepicker>

    <div
      v-if="type==='Password'"
      class="pass-contain"
    >
      <input
        :type="isPassVisible ? 'text' : 'password'"
        :disabled="disabled"
        class="input pass-in"
        :placeholder="placeholder"
        :value="modelValue"
        :maxlength="maxlength"
        :readonly="readonly"
        :autocomplete="autocomplete ? 'on' : 'new-password'"
        @input="e => input(e.target.value)"
      >
      <AuIcon
        v-if="!isEdge"
        class="eye-icon"
        :icon="isPassVisible ? 'eye-open' : 'eye-close'"
        @click="showPass"
      />
    </div>

    <div
      v-if="isDrawBorderType"
      ref="border"
      class="input_block"
      :style="type === 'TextArea' ? textAreaStyle : ''"
      :class="{'required-error' : reqError, 'pass-height': type==='Password'}"
    >
      <img
        v-if="icon !== ''"
        :src="`/svg/light/${icon}.svg`"
        alt="no_img"
      >

      <ImaskInput
        v-if="type === 'Mask'"
        :model-value="modelValue"
        class="border_off input"
        :placeholder="placeholder"
        :readonly="readonly"
        :mask="mask"
        :max="max"
        :min="min"
        :prepare="prepare"
        @update:modelValue="input"
        @blur="$emit('blur')"
      />

      <AuDecimalInput
        v-if="type === 'Number'"
        class="border_off input"
        :decimal="decimal"
        :indent="indent"
        :value="modelValue"
        :max="max"
        :min="min"
        :placeholder="placeholder"
        @input="value => input(value)"
      />

      <label
        v-if="type === 'File'"
        class="input-file"
      >
        <input
          type="file"
          accept="image/png, image/jpeg"
          class="border_off input"
          :placeholder="placeholder"
          :value="modelValue"
          :disabled="disabled"
          @input="e => input(e.target.files[0])"
        >
        <span>{{ uploadedFileName ? uploadedFileName : fileLabel }}</span>
      </label>

      <input
        v-if="type === 'NullableNumber'"
        type="text"
        :disabled="disabled"
        class="border_off input"
        :placeholder="placeholder"
        :value="modelValue"
        :maxlength="maxlength"
        :readonly="readonly"
        @input="e => onInput(e)"
      >

      <input
        v-if="type === 'Text'"
        type="text"
        :disabled="disabled"
        class="border_off input"
        :placeholder="placeholder"
        :value="modelValue"
        :maxlength="maxlength"
        :readonly="readonly"
        @input="e => input(e.target.value)"
      >

      <AuIcon
        v-if="iconEnd !== ''"
        :icon="iconEnd"
      />

      <textarea
        v-if="type === 'TextArea'"
        ref="description"
        class="is-text-area"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="e => {input(e.target.value); autoGrow(e);}"
        @focus="e => {input(e.target.value); autoGrow(e);}"
      />
      <div
        v-if="clearable && showFlush"
        class="reset-field"
        @click="flushField"
      >
        ×
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { IMaskComponent } from "vue-imask";
import { mapGetters } from "vuex";

import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  name: "AuInput",

  components: {
    "ImaskInput": IMaskComponent,
    "Datepicker": Datepicker
  },

  props: {
    icon: {
      type: String,
      default: ""
    },
    iconEnd: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: ""
    },
    modelValue: {
      type: String,
      default: ""
    },
    tooltip: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "Text",
      validator: function (value) {
        let validationResult =
          [
            "Text",
            "Number",
            "NullableNumber",
            "Mask",
            "TextArea",
            "MarkDown",
            "Date",
            "File",
            "Password"
          ].indexOf(value) !== -1;

        if (!validationResult) {
          console.error(value);
        }

        return validationResult;
      }
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    clearable: {
      type: Boolean,
      default: false
    },
    textAreaHeigth: {
      type: Number,
      default: 150
    },
    mask: {
      type: [RegExp, Function],
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    min: {
      type: Number,
      default: null
    },
    notOnlyInteger: {
      type: Boolean,
      default: false
    },
    minDate: {
      type: String,
      default: ""
    },
    maxDate: {
      type: String,
      default: ""
    },
    format: {
      type: Function,
      default: v => v
    },
    uploadedFileName: {
      type: String,
      default: ""
    },
    borderRadius: {
      type: String,
      default: "12"
    },
    decimal: {
      type: Number,
      default: 0
    },
    indent: {
      type: Number,
      default: 2
    },
    maxlength: {
      type: Number,
      default: 255
    },
    hasError: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    enableTimePicker: {
      type: Boolean,
      default: false
    },
    enableMinutes: {
      type: Boolean,
      default: true
    },
    autocomplete: {
      type: Boolean,
      default: true
    },
    prepare: {
      type: Function,
      default: value => value
    }
  },

  data() {
    return {
      fileLabel: this.$t("news.form.download"),
      isPassVisible: false,
      isDirty: false
    };
  },

  computed: {
    ...mapGetters({
      language: "language"
    }),

    textAreaStyle() {
      return `height: ${this.textAreaHeigth}px; min-height: ${this.textAreaHeigth}px`;
    },

    inputStyle() {
      return {
        "--border-radius": this.borderRadius + "px"
      };
    },

    reqError() {
      return this.hasError
      || (this.isDirty && this.required
      && (this.type == "File" ? this.fileLabel == this.$t("news.form.download")
      && !this.uploadedFileName : _.isEmpty(this.modelValue)))
      || (this.isDirty && this.required && this.min && this.min > this.modelValue?.length);
    },

    reqErrorDate() {
      return this.hasError || (this.isDirty && this.required && !this.modelValue);
    },

    showFlush() {
      return !_.isEmpty(this.modelValue);
    },

    isLightTheme() {
      return this.$store.getters.isLightTheme;
    },

    isEdge() {
      return /Edg/.test(window.navigator.userAgent);
    },

    isDrawBorderType() {
      return this.type === "Text" || this.type === "Number" || this.type === "Mask"
          || this.type === "TextArea" || this.type === "File" || this.type === "NullableNumber";
    }
  },

  watch: {
    language() {
      this.fileLabel = this.$t("news.form.download");
    }
  },

  mounted() {
    this.$nextTick(() => {
      const textarea = this.$refs.description;
      if (textarea) {
        this.autoGrow(textarea);
      }
    });
  },

  methods: {
    autoGrow(e) {
      this.$refs.description.style.height = "auto";
      this.$refs.border.style.height = "auto";
      if (e?.scrollHeight) {
        this.$refs.description.style.height = e.scrollHeight + "px";
        this.$refs.border.style.height = e.scrollHeight + "px";
      }
      if (e?.target?.scrollHeight) {
        this.$refs.description.style.height = e.target.scrollHeight + "px";
        this.$refs.border.style.height = e.target.scrollHeight + "px";
      }
    },

    updateValue(value) {
      if (value === "" && this.modelValue === null) {
        return;
      }

      this.isDirty = true;
      this.$emit("update:modelValue", value);
    },

    input(value) {
      this.updateValue(value);

      if (this.type == "File") {
        this.fileLabel = value.name;
      }
    },

    onInput(event) {
      let pettern = this.notOnlyInteger ? /[^0-9.]|[.](?=.*[.])|^[.]/g : /[^0-9]/g;
      let cleanedValue = event.target.value.replace(pettern, "");

      if (cleanedValue === "") {
        event.target.value = "";
        this.updateValue("");
        return;
      }
      if (this.max !== null) {
        cleanedValue = Math.min(Number(cleanedValue), this.max).toString();
      }

      if (cleanedValue === this.modelValue) {
        this.updateValue("");
        this.$nextTick(() => {
          this.updateValue(cleanedValue);
          event.target.value = cleanedValue;
        });
      }
      else {
        this.updateValue(cleanedValue);
        event.target.value = cleanedValue;
      }
    },

    flushField() {
      if (this.type === "TextArea") {
        this.$refs.description.style.height = "auto";
        this.$refs.border.style.height = "auto";
      }
      this.updateValue("");
    },

    showPass() {
      this.isPassVisible = !this.isPassVisible;
      this.updateValue(this.modelValue);
    }
  }
};
</script>

<style lang="scss">
.date-input {
  border: 1px solid var(--color-70);
  border-radius: var(--border-radius);

  input {
    border-radius: var(--border-radius);
    border: none;
    padding: 13px 15px 13px 12px;
    height: 44px;
    color: var(--color-0);
  }
  .dp__input_icon {
    right: 0px;
    left: auto;
  }
  .dp--clear-btn {
    right: 35px;
  }
}
.input-wrapper {
  width: 100%;
}
.input {
  color: var(--color-50);
  &[readonly] {
    color: var(--color-30);
  }
  &[disabled] {
    &::placeholder {
      color: var(--color-40);
    }
  }
  &[type="file"] {
    background: red;
  }

  &::-webkit-input-placeholder {
    color: var(--color-50);
  }

  &::-moz-placeholder {
    color: var(--color-50);
  }

  &::-ms-placeholder {
    color: var(--color-50);
  }

  &::placeholder {
    color: var(--color-50);
    // padding-left: 5px;
  }
}

.input-label {
  font-size: 14px;
  margin-bottom: 8px;
  color: var(--color-50);
  &[disabled="true"] {
    color: var(--color-40);
  }
}
.input_block {
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: center;
  border: 1px solid var(--color-70);
  border-radius: var(--border-radius);
  font-weight: 450;
  font-size: 13px;
  height: 44px;
  width: auto;
  position: relative;
  &:has(>input[disabled]),
  &:has(>.input-file > input[disabled]) {
    border: 1px solid var(--color-40) !important;
  }
  &:has(>.input-file) {
    justify-content: center;
  }
}
.input_block > img {
  padding: 0 12px;
}

.border_off{
  border: none;
  outline: none;
  padding-left: 12px;
}
.required-error {
  border-color: #ff000069;
}
.reset-field {
  position: absolute;
  right: 15px;
  top: 10px;
  user-select: none;
  color: var(--color-50);
  &:hover {
    cursor: pointer;
  }
}
.is-text-area{
  width: 100%;
  padding: 12px;
  resize: none;
  border: 0;
  height: 100%;
  outline: none;
  overflow-y: hidden;
  :focus {
    outline: none;
  }
  &::placeholder {
    color: var(--color-50);
  }
}

.input-file {
	position: relative;
	display: inline-block;
  overflow: hidden;
  width: 100%;
  span {
    position: relative;
    display: inline-block;
    cursor: pointer;
    outline: none;
    text-decoration: none;
    font-size: 13px;
    vertical-align: middle;
    color: var(--text-color);
    text-align: center;
    border-radius: 4px;
    line-height: 22px;
    height: 40px;
    padding: 10px 20px;
    box-sizing: border-box;
    border: none;
    margin: 0;
    transition: background-color 0.2s;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }
  input[type=file] {
    position: absolute;
    z-index: -1;
    opacity: 0;
    display: block;
    width: 0;
    height: 0;
    &:disabled + span {
      color: var(--color-40);
      user-select: none;
      cursor: auto;
    }
  }
}

.pass-contain {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
}
.eye-icon {
  position: absolute;
  right: 15px;
  cursor: pointer;
  width: 20px;
}
.pass-in {
  border-radius: 10px;
  border: 1px solid var(--color-70);
  padding: 15px;
}
</style>

